import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import AdminPage from './AdminPage';
import ImageUpload from '../../components/common/ImageUpload';
import { ImageItem } from '../../types/upload';
import { getStaticFileUrl } from '../../config/apiConfig';
import {
  getAdminReviews,
  getAdminStatistics,
  toggleReviewStatus,
  updateReview,
  deleteReview,
  adminCreateReview,
} from '../../services/reviewService';
import { AdminReviewDto, ReviewStatisticsDto, AdminReviewFilterDto, UpdateReviewDto, AdminCreateReviewDto, REVIEW_HIGHLIGHTS, ORDER_AGAIN_OPTIONS } from '../../types/review';
import StarRating from '../../components/frontend/reviews/StarRating';
import { amazonColors } from '../../config/amazonTheme';

interface ReviewFormData {
  rating?: number;
  reviewerName?: string;
  comment?: string;
  highlights?: string[];
  wouldOrderAgain?: string;
  photoUrls?: string[];
  isEnabled?: boolean;
  adminNotes?: string;
}

interface ReviewFormFieldsProps<T extends ReviewFormData = ReviewFormData> {
  form: T;
  onChange: (updater: (prev: T) => T) => void;
  requiredName?: boolean;
}

// Convert string URLs to ImageItem[] for the ImageUpload component
const urlsToImageItems = (urls: string[]): ImageItem[] =>
  urls.map((url) => ({
    id: crypto.randomUUID(),
    url,
    isMain: false,
  }));

// Convert ImageItem[] back to string URLs, filtering out uploading items
const imageItemsToUrls = (items: ImageItem[]): string[] =>
  items.filter((item) => !item.isUploading && !item.error).map((item) => item.url);

const ReviewFormFields = <T extends ReviewFormData>({ form, onChange, requiredName }: ReviewFormFieldsProps<T>) => {
  const { t } = useTranslation();
  // Maintain ImageItem[] state locally to preserve IDs and upload state
  const [imageItems, setImageItems] = useState<ImageItem[]>(() =>
    urlsToImageItems(form.photoUrls || [])
  );

  // Track the last URLs we synced so we can detect truly external changes
  const lastSyncedUrls = useRef<string[]>(form.photoUrls || []);

  // Re-initialize only when photoUrls actually change externally (e.g., dialog opened with different review)
  useEffect(() => {
    const current = form.photoUrls || [];
    // Compare array contents — skip if same as what we last synced
    if (
      current.length === lastSyncedUrls.current.length &&
      current.every((url, i) => url === lastSyncedUrls.current[i])
    ) {
      return;
    }
    lastSyncedUrls.current = current;
    setImageItems(urlsToImageItems(current));
  }, [form.photoUrls]);

  const handleImagesChange = useCallback(
    (newImages: ImageItem[]) => {
      // Update local state (preserves IDs, upload progress, blob previews)
      setImageItems(newImages);
      // Sync finished URLs to parent form
      const urls = imageItemsToUrls(newImages);
      lastSyncedUrls.current = urls;
      onChange((prev) => ({
        ...prev,
        photoUrls: urls,
      }));
    },
    [onChange]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          {t("admin.reviews.rating")}{requiredName ? ' *' : ''}
        </Typography>
        <StarRating
          value={form.rating || 0}
          onChange={(val) => onChange((prev) => ({ ...prev, rating: val }))}
          size="medium"
        />
      </Box>
      <TextField
        label={t("admin.reviews.reviewerName")}
        required={requiredName}
        size="small"
        value={form.reviewerName || ''}
        onChange={(e) => onChange((prev) => ({ ...prev, reviewerName: e.target.value }))}
      />
      <TextField
        label={t("admin.reviews.comment")}
        multiline
        rows={3}
        size="small"
        value={form.comment || ''}
        onChange={(e) => onChange((prev) => ({ ...prev, comment: e.target.value }))}
      />
      <FormControl size="small">
        <InputLabel>{t("admin.reviews.wouldOrderAgain")}</InputLabel>
        <Select
          value={form.wouldOrderAgain || ''}
          label={t("admin.reviews.wouldOrderAgain")}
          onChange={(e) => onChange((prev) => ({ ...prev, wouldOrderAgain: e.target.value }))}
        >
          <MenuItem value="">{t("common.none")}</MenuItem>
          {ORDER_AGAIN_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box>
        <Typography variant="caption" color="text.secondary">{t("admin.reviews.highlights")}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {REVIEW_HIGHLIGHTS.map((h) => (
            <Chip
              key={h}
              label={h}
              size="small"
              variant={form.highlights?.includes(h) ? 'filled' : 'outlined'}
              color={form.highlights?.includes(h) ? 'primary' : 'default'}
              onClick={() => {
                onChange((prev) => {
                  const current = prev.highlights || [];
                  return {
                    ...prev,
                    highlights: current.includes(h)
                      ? current.filter((x) => x !== h)
                      : [...current, h],
                  };
                });
              }}
            />
          ))}
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {t("admin.reviews.images")}
        </Typography>
        <ImageUpload
          images={imageItems}
          onChange={handleImagesChange}
          maxImages={5}
          showMainToggle={false}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Switch
          checked={form.isEnabled ?? true}
          onChange={(e) => onChange((prev) => ({ ...prev, isEnabled: e.target.checked }))}
          color="success"
        />
        <Typography variant="body2">{t("admin.reviews.published")}</Typography>
      </Box>
      <TextField
        label={t("admin.reviews.adminNotes")}
        multiline
        rows={2}
        size="small"
        value={form.adminNotes || ''}
        onChange={(e) => onChange((prev) => ({ ...prev, adminNotes: e.target.value }))}
        placeholder={t("admin.reviews.adminNotesPlaceholder")}
      />
    </Box>
  );
};

const ReviewsPage: React.FC = () => {
  const { t } = useTranslation();
  // State
  const [reviews, setReviews] = useState<AdminReviewDto[]>([]);
  const [statistics, setStatistics] = useState<ReviewStatisticsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [filters, setFilters] = useState<AdminReviewFilterDto>({
    pageNumber: 1,
    pageSize: 10,
    sortBy: 'createdAt',
    sortDirection: 'desc',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState<number | ''>('');
  const [enabledFilter, setEnabledFilter] = useState<boolean | ''>('');

  // Modals
  const [selectedReview, setSelectedReview] = useState<AdminReviewDto | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<UpdateReviewDto>({});

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteReviewId, setDeleteReviewId] = useState<string | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<AdminCreateReviewDto>({
    rating: 5,
    reviewerName: '',
    comment: '',
    photoUrls: [],
    highlights: [],
    wouldOrderAgain: '',
    isEnabled: true,
    adminNotes: '',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const filterPayload: AdminReviewFilterDto = {
        ...filters,
        searchTerm: searchTerm || undefined,
        rating: ratingFilter !== '' ? ratingFilter : undefined,
        isEnabled: enabledFilter !== '' ? enabledFilter : undefined,
      };
      const response = await getAdminReviews(filterPayload);
      setReviews(response.items);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      showSnackbar(t('admin.reviews.fetchFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm, ratingFilter, enabledFilter]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      const stats = await getAdminStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleToggleStatus = async (reviewId: string) => {
    try {
      await toggleReviewStatus(reviewId);
      setReviews((prev) =>
        prev.map((r) =>
          r.id === reviewId ? { ...r, isEnabled: !r.isEnabled } : r
        )
      );
      showSnackbar(t('admin.reviews.statusUpdated'), 'success');
      fetchStatistics();
    } catch (error) {
      showSnackbar(t('admin.reviews.statusUpdateFailed'), 'error');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedReview) return;
    try {
      const updated = await updateReview(selectedReview.id, editForm);
      setReviews((prev) =>
        prev.map((r) => (r.id === selectedReview.id ? updated : r))
      );
      setEditOpen(false);
      showSnackbar(t('admin.reviews.reviewUpdated'), 'success');
      fetchStatistics();
    } catch (error) {
      showSnackbar(t('admin.reviews.updateFailed'), 'error');
    }
  };

  const handleOpenEdit = (review: AdminReviewDto) => {
    setSelectedReview(review);
    setEditForm({
      rating: review.rating,
      reviewerName: review.reviewerName,
      comment: review.comment || '',
      highlights: [...review.highlights],
      wouldOrderAgain: review.wouldOrderAgain || '',
      photoUrls: [...review.photoUrls],
      isEnabled: review.isEnabled,
      adminNotes: review.adminNotes || '',
    });
    setEditOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteReviewId) return;
    try {
      await deleteReview(deleteReviewId);
      setReviews((prev) => prev.filter((r) => r.id !== deleteReviewId));
      setTotalCount((prev) => prev - 1);
      setDeleteConfirmOpen(false);
      setDeleteReviewId(null);
      showSnackbar(t('admin.reviews.reviewDeleted'), 'success');
      fetchStatistics();
    } catch (error) {
      showSnackbar(t('admin.reviews.deleteFailed'), 'error');
    }
  };

  const handleCreate = async () => {
    try {
      await adminCreateReview(createForm);
      setCreateOpen(false);
      setCreateForm({
        rating: 5,
        reviewerName: '',
        comment: '',
        photoUrls: [],
        highlights: [],
        wouldOrderAgain: '',
        isEnabled: true,
        adminNotes: '',
      });
      showSnackbar(t('admin.reviews.reviewCreated'), 'success');
      fetchReviews();
      fetchStatistics();
    } catch (error) {
      showSnackbar(t('admin.reviews.createFailed'), 'error');
    }
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageNumber: 1,
    }));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, pageNumber: 1 }));
    fetchReviews();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <AdminPage>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t("admin.reviews.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("admin.reviews.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          {t("admin.reviews.createReview")}
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="primary">
                {statsLoading ? <CircularProgress size={24} /> : statistics?.totalReviews || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("admin.reviews.totalReviews")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {statsLoading ? <CircularProgress size={24} /> : statistics?.enabledReviews || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("admin.reviews.published")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: amazonColors.orange }}>
                  {statsLoading ? <CircularProgress size={24} /> : statistics?.averageRating?.toFixed(1) || '0.0'}
                </Typography>
                <StarIcon sx={{ color: amazonColors.orange, fontSize: 28 }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t("admin.reviews.avgRating")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {statsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  (statistics?.totalReviews || 0) - (statistics?.enabledReviews || 0)
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("admin.reviews.disabled")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <form onSubmit={handleSearchSubmit} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              size="small"
              placeholder={t("admin.reviews.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <Button
            startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {t("admin.reviews.filters")}
          </Button>
          <IconButton onClick={() => { fetchReviews(); fetchStatistics(); }}>
            <RefreshIcon />
          </IconButton>
        </Box>

        <Collapse in={showFilters}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t("admin.reviews.rating")}</InputLabel>
              <Select
                value={ratingFilter}
                label={t("admin.reviews.rating")}
                onChange={(e) => {
                  setRatingFilter(e.target.value as number | '');
                  setFilters((prev) => ({ ...prev, pageNumber: 1 }));
                }}
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                {[5, 4, 3, 2, 1].map((r) => (
                  <MenuItem key={r} value={r}>{t("admin.reviews.nStars", { count: r })}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t("admin.reviews.status")}</InputLabel>
              <Select
                value={enabledFilter}
                label={t("admin.reviews.status")}
                onChange={(e) => {
                  setEnabledFilter(e.target.value as boolean | '');
                  setFilters((prev) => ({ ...prev, pageNumber: 1 }));
                }}
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                <MenuItem value={true as any}>{t("admin.reviews.published")}</MenuItem>
                <MenuItem value={false as any}>{t("admin.reviews.disabled")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Collapse>
      </Paper>

      {/* Reviews Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.reviews.order")}</TableCell>
              <TableCell>{t("admin.reviews.customer")}</TableCell>
              <TableCell>{t("admin.reviews.rating")}</TableCell>
              <TableCell>{t("admin.reviews.comment")}</TableCell>
              <TableCell>{t("admin.reviews.orderAgain")}</TableCell>
              <TableCell>{t("admin.reviews.date")}</TableCell>
              <TableCell align="center">{t("admin.reviews.published")}</TableCell>
              <TableCell align="center">{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{t("admin.reviews.noReviewsFound")}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((review) => (
                <TableRow key={review.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      #{review.orderNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{review.reviewerName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {review.customerEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StarRating value={review.rating} readonly size="small" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {review.comment || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {review.wouldOrderAgain ? (
                      <Chip
                        label={review.wouldOrderAgain}
                        size="small"
                        color={review.wouldOrderAgain === 'Yes' ? 'success' : review.wouldOrderAgain === 'Maybe' ? 'warning' : 'error'}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(review.createdAt)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={review.isEnabled}
                      onChange={() => handleToggleStatus(review.id)}
                      color="success"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("admin.reviews.viewDetails")}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedReview(review);
                          setDetailsOpen(true);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.edit")}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(review)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeleteReviewId(review.id);
                          setDeleteConfirmOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={filters.pageNumber - 1}
          onPageChange={handlePageChange}
          rowsPerPage={filters.pageSize}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      {/* Review Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        {selectedReview && (
          <>
            <DialogTitle>
              {t("admin.reviews.reviewDetails")} - #{selectedReview.orderNumber}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t("admin.reviews.customer")}</Typography>
                  <Typography variant="body1">{selectedReview.customerName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t("admin.reviews.reviewerName")}</Typography>
                  <Typography variant="body1">{selectedReview.reviewerName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t("admin.reviews.email")}</Typography>
                  <Typography variant="body1">{selectedReview.customerEmail}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t("admin.reviews.rating")}</Typography>
                  <Box>
                    <StarRating value={selectedReview.rating} readonly size="small" />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">{t("admin.reviews.comment")}</Typography>
                  <Typography variant="body1">{selectedReview.comment || t('admin.reviews.noComment')}</Typography>
                </Grid>
                {selectedReview.photoUrls && selectedReview.photoUrls.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.reviews.photos", { count: selectedReview.photoUrls.length })}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                        gap: 1,
                      }}
                    >
                      {selectedReview.photoUrls.map((photoUrl, index) => (
                        <Box
                          key={index}
                          component="img"
                          src={getStaticFileUrl(photoUrl)}
                          alt={`Review photo ${index + 1}`}
                          sx={{
                            width: '100%',
                            aspectRatio: '1',
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                {selectedReview.highlights && selectedReview.highlights.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">{t("admin.reviews.highlights")}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {selectedReview.highlights.map((h) => (
                        <Chip key={h} label={h} size="small" />
                      ))}
                    </Box>
                  </Grid>
                )}
                {selectedReview.wouldOrderAgain && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">{t("admin.reviews.wouldOrderAgain")}</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedReview.wouldOrderAgain}
                        size="small"
                        color={selectedReview.wouldOrderAgain === 'Yes' ? 'success' : selectedReview.wouldOrderAgain === 'Maybe' ? 'warning' : 'error'}
                      />
                    </Box>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t("admin.reviews.status")}</Typography>
                  <Box>
                    <Chip
                      label={selectedReview.isEnabled ? t('admin.reviews.published') : t('admin.reviews.disabled')}
                      color={selectedReview.isEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t("admin.reviews.verified")}</Typography>
                  <Box>
                    <Chip
                      label={selectedReview.isVerified ? t('admin.reviews.verified') : t('admin.reviews.unverified')}
                      color={selectedReview.isVerified ? 'primary' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
                {selectedReview.adminNotes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">{t("admin.reviews.adminNotes")}</Typography>
                    <Typography variant="body2">{selectedReview.adminNotes}</Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setDetailsOpen(false);
                  handleOpenEdit(selectedReview);
                }}
              >
                {t("common.edit")}
              </Button>
              <Button onClick={() => setDetailsOpen(false)}>{t("common.close")}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Review Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admin.reviews.editReview")}</DialogTitle>
        <DialogContent>
          <ReviewFormFields form={editForm} onChange={setEditForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Review Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admin.reviews.createReview")}</DialogTitle>
        <DialogContent>
          <ReviewFormFields<AdminCreateReviewDto> form={createForm} onChange={setCreateForm} requiredName />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!createForm.reviewerName.trim() || !createForm.rating}
          >
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t("admin.reviews.deleteReview")}</DialogTitle>
        <DialogContent>
          <Typography>{t("admin.reviews.deleteConfirmation")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminPage>
  );
};

export default ReviewsPage;
